import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { IBetCloseModel, IRoundFinishModel, IRoundReportModel, IRoundStartModel } from '../interfaces/round';
import { gameStore, userStore } from '../stores';
import { IGameInitializeModel } from '../interfaces/game';
import { betService, gameService, roundService } from '../services';
import { IBetModel, IDoBet, IReBetModel, IUpdateBet } from '../interfaces/bet';
import { hideReconnectingModal, showReconnectingModal } from '../../modules/reconnect-modal/ReconnectModal';
import { showConnectionLostModal } from '../../modules/connection-lost-modal/ConnectionLostModal';

class GameConnection {
  private connection: HubConnection | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 15;
  private reconnectTimeout = 3000;

  connect = async () => {
    await this.initializeConnection();
  }

  doBet = (data: IDoBet) => {
    return this.connection?.invoke('Bet', data);
  }

  updateBet = (data: IUpdateBet) => {
    return this.connection?.invoke('UpdateBet', data);
  }

  reBet = (roundId: number, currentRoundId: number) => {
    return this.connection?.invoke('ReBet', { roundId, currentRoundId });
  }

  private initializeConnection = async () => {
    this.connection = new HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BASE_URL}/hubs/game`, { accessTokenFactory: () => userStore.accessToken })
      .build();

    this.connection.onclose(this.handleConnectionClosed);

    this.connection.on('RoundStart', (data: IRoundStartModel) => {
      roundService.startRound(data);
    });
    this.connection.on('RoundFinish', (data: IRoundFinishModel) => {
      roundService.finishRound(data);
    });
    this.connection.on('BetClose', (_: IBetCloseModel) => {
      roundService.closeBets();
    });
    this.connection.on('RoundReport', (data: IRoundReportModel) => {
      roundService.reportRound(data);
    });
    this.connection.on('InitializeGame', (data: IGameInitializeModel) => {
      gameService.initializeData(data);
    });
    this.connection.on('Bet', (data: IBetModel) => {
      betService.handleBet(data);
    });
    this.connection.on('ReBet', (data: IReBetModel) => {
      betService.handleReBet(data);
    });
    this.connection.on('UpdateBet', (data: IUpdateBet) => {
      betService.handleBetUpdate(data);
    });
    this.connection.on('BetError', (error: string) => {
      betService.handleBetError(error);
    });
    this.connection.on('ReBetError', (error: string) => {
      betService.handleReBetError(error);
    });
    this.connection.on('UpdateBetError', (error: string) => {
      betService.handleUpdateBetError(error);
    });
    this.connection.on('InitializeGameError', (error: string) => {
      gameService.handleInitializeGameError(error);
      setTimeout(() => this.initializeGame(), 500);
    });

    await this.connection.start()
      .then(this.initializeGame)
      .catch(this.handleConnectionClosed);
  }

  private initializeGame = () => {
    const roomId = gameStore.gameId === 8 ? 1 : 2;
    this.connection?.invoke('InitializeGame', { roomId, partnerId: gameStore.partnerId });
  }

  private handleConnectionClosed = () => {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log('Reconnecting attempt', this.reconnectAttempts);
      showReconnectingModal();

      setTimeout(() => {
        this.connection?.start()
          .then(() => {
            this.reconnectAttempts = 0;
            hideReconnectingModal();
            this.initializeGame();
          })
          .catch(reconnectError => {
            this.handleConnectionClosed();
            console.log('Reconnect error:', reconnectError);
          });
      }, this.reconnectTimeout);
    } else {
      showConnectionLostModal();
      console.log('Max reconnect attempts reached.');
    }
  }
}

export const gameConnection = new GameConnection();
